import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { NEW_ORDER_SIGNAL_KEY_PREFIX } from 'src/constants/key_constants';

/**
 * Returns whether the current edit page was just created in this session.
 *
 * Combines two sources:
 *  1. sessionStorage flash — set by the CREATE action via `signalNewOrder`
 *     before `throw redirect(...)`. Consumed exactly once on first mount;
 *     a page reload correctly returns false.
 *  2. router location state — set by client-side `navigate` calls that
 *     pass `{ state: { isNewOrder: true } }` (e.g. the "Create Order" button).
 *
 * The result is captured in a ref so it is stable for the lifetime of the
 * mount and does not change on re-renders.
 */
export function useNewOrderSignal(orderId: string): boolean {
  const location = useLocation();

  const isNewOrderRef = useRef<boolean>(
    consumeNewOrderSignal(orderId) || !!location.state?.isNewOrder,
  );

  return isNewOrderRef.current;
}

/**
 * Reads and immediately deletes the new-order signal for the given order id.
 * Returns true only on the very first call after `signalNewOrder` - a page
 * reload (or any subsequent mount) returns false because the key is gone.
 */
const consumeNewOrderSignal = (orderId: string): boolean => {
  const key = `${NEW_ORDER_SIGNAL_KEY_PREFIX}${orderId}`;
  const isNew = sessionStorage.getItem(key) === '1';
  if (isNew) sessionStorage.removeItem(key);
  return isNew;
};
